/**
 * Dashboard navigation: one source for the sidebar groups and the top bar breadcrumb.
 *
 * Every nav icon is from the Lucide set (thin, monochrome line icons, bundled locally via `npm run icons`), so the
 * sidebar reads as one family. Add a page here and it gets a sidebar row (if it is in a group) and a breadcrumb.
 */

export interface NavItem {
  label: string;
  icon: string;
  link: string;
  /** Small "New" pill after the label (a blue dot on the icon when the sidebar is collapsed). Remove the key to retire it. */
  badge?: "New";
  /** Hidden entirely unless this flag resolves enabled for the current user. */
  flagKey?: string;
  /** A page outside the app (opens in a new tab), e.g. Bull Board on the API host. */
  external?: boolean;
}
export interface NavGroup {
  heading?: string;
  items: NavItem[];
}

// Overview and Templates, then Tools (the work-doing pages), Automation (recurring work) and Account.
export const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { label: "Overview", icon: "lucide:house", link: "/dashboard" },
      { label: "Templates", icon: "lucide:layout-template", link: "/dashboard/templates", badge: "New" },
    ],
  },
  {
    heading: "Tools",
    items: [
      { label: "Search", icon: "lucide:search", link: "/dashboard/search", flagKey: "search" },
      { label: "Scrape", icon: "lucide:scan-text", link: "/dashboard/playground" },
      { label: "Parse", icon: "lucide:file-text", link: "/dashboard/parse" },
      { label: "Map", icon: "lucide:network", link: "/dashboard/map" },
      { label: "Crawl", icon: "lucide:waypoints", link: "/dashboard/crawling-playground" },
    ],
  },
  {
    heading: "Automation",
    items: [
      { label: "Presets", icon: "lucide:bookmark", link: "/dashboard/presets" },
      { label: "Monitors", icon: "lucide:radar", link: "/dashboard/monitors", badge: "New" },
    ],
  },
  {
    heading: "Account",
    items: [
      { label: "Billing", icon: "lucide:credit-card", link: "/dashboard/billing" },
      { label: "Logs", icon: "lucide:scroll-text", link: "/dashboard/history" },
      { label: "Usage", icon: "lucide:chart-line", link: "/dashboard/usage" },
      { label: "API keys", icon: "lucide:key-round", link: "/dashboard/api-keys" },
    ],
  },
];

// The API host without its /api suffix; Bull Board is mounted at /admin/queues there (admins only, same session cookie).
const API_ORIGIN = (process.env.NEXT_PUBLIC_API_URL || "https://api.spidra.io/api").replace(/\/api\/?$/, "");
export const QUEUES_URL = `${API_ORIGIN}/admin/queues`;

// Admin: Overview, then Activity (every job across the platform), Health (is it working), Management and System.
export const ADMIN_NAV_GROUPS: NavGroup[] = [
  { items: [{ label: "Overview", icon: "lucide:layout-dashboard", link: "/admin" }] },
  {
    heading: "Activity",
    items: [
      { label: "Scrapes", icon: "lucide:scan-text", link: "/admin/scrapes" },
      { label: "Searches", icon: "lucide:search", link: "/admin/searches" },
      { label: "Crawls", icon: "lucide:waypoints", link: "/admin/crawls" },
      { label: "Maps", icon: "lucide:network", link: "/admin/maps" },
      { label: "Parses", icon: "lucide:file-text", link: "/admin/parses" },
      { label: "Batches", icon: "lucide:layers", link: "/admin/batches" },
    ],
  },
  {
    heading: "Health",
    items: [
      { label: "Site readers", icon: "lucide:activity", link: "/admin/site-readers" },
      { label: "Queues", icon: "lucide:list-ordered", link: QUEUES_URL, external: true },
    ],
  },
  {
    heading: "Management",
    items: [
      { label: "Users", icon: "lucide:users", link: "/admin/users" },
      { label: "Monitors", icon: "lucide:radar", link: "/admin/monitors" },
      { label: "Templates", icon: "lucide:layout-template", link: "/admin/templates" },
    ],
  },
  {
    heading: "System",
    items: [
      { label: "Email", icon: "lucide:mail", link: "/admin/email" },
      { label: "Changelog", icon: "lucide:megaphone", link: "/admin/changelog" },
      { label: "Feature flags", icon: "lucide:flag", link: "/admin/feature-flags" },
      { label: "Proxy domains", icon: "lucide:globe-lock", link: "/admin/proxy-blocked-domains" },
      { label: "Proxy config", icon: "lucide:server", link: "/admin/proxy-config" },
      { label: "Search engines", icon: "lucide:scan-search", link: "/admin/search-engines" },
    ],
  },
];

export interface Crumb {
  label: string;
  icon: string;
  href?: string;
}

/** Pages that are not sidebar rows (or are detail pages under one). Keyed by Next's router.pathname. */
const EXTRA: Record<string, Crumb[]> = {
  "/dashboard/batch": [
    { label: "Logs", icon: "lucide:scroll-text", href: "/dashboard/history?tab=batch" },
    { label: "Batch", icon: "lucide:layers" },
  ],
  "/dashboard/settings": [{ label: "Settings", icon: "lucide:settings" }],
  "/dashboard/monitors/[monitorId]": [
    { label: "Monitors", icon: "lucide:radar", href: "/dashboard/monitors" },
    { label: "Monitor", icon: "lucide:radar" },
  ],
  "/dashboard/templates/[slug]": [
    { label: "Templates", icon: "lucide:layout-template", href: "/dashboard/templates" },
    { label: "Template", icon: "lucide:layout-template" },
  ],
  "/dashboard/ui-kit": [{ label: "UI kit", icon: "lucide:component" }],
  "/admin/changelog/new": [
    { label: "Changelog", icon: "lucide:megaphone", href: "/admin/changelog" },
    { label: "New entry", icon: "lucide:megaphone" },
  ],
  "/admin/changelog/[uuid]": [
    { label: "Changelog", icon: "lucide:megaphone", href: "/admin/changelog" },
    { label: "Entry", icon: "lucide:megaphone" },
  ],
  "/admin/changelog/banner": [
    { label: "Changelog", icon: "lucide:megaphone", href: "/admin/changelog" },
    { label: "Banner", icon: "lucide:megaphone" },
  ],
  "/admin/marketplace": [{ label: "Marketplace presets", icon: "lucide:bookmark" }],
  "/admin/marketplace/new": [
    { label: "Marketplace presets", icon: "lucide:bookmark", href: "/admin/marketplace" },
    { label: "New preset", icon: "lucide:bookmark" },
  ],
  "/admin/marketplace/[uuid]": [
    { label: "Marketplace presets", icon: "lucide:bookmark", href: "/admin/marketplace" },
    { label: "Preset", icon: "lucide:bookmark" },
  ],
};

export function isNavActive(link: string, pathname: string): boolean {
  return link === "/dashboard" || link === "/admin" ? pathname === link : pathname === link || pathname.startsWith(link + "/");
}

/** Breadcrumb trail for a route. The last crumb is the current page (no href). */
export function getCrumbs(pathname: string): Crumb[] {
  if (EXTRA[pathname]) return EXTRA[pathname];
  const admin = pathname === "/admin" || pathname.startsWith("/admin/");
  const root = admin ? "/admin" : "/dashboard";
  const items = (admin ? ADMIN_NAV_GROUPS : NAV_GROUPS).flatMap(g => g.items).filter(i => !i.external);
  const exact = items.find(i => i.link === pathname);
  if (exact) return [{ label: exact.label, icon: exact.icon }];
  const parent = items.filter(i => i.link !== root).find(i => pathname.startsWith(i.link + "/"));
  if (parent) return [{ label: parent.label, icon: parent.icon }];
  return [{ label: "Overview", icon: admin ? "lucide:layout-dashboard" : "lucide:house" }];
}
